import JsSIP from 'jssip/lib/JsSIP';
import type { RTCSession } from 'jssip/lib/RTCSession';
import type { SubscriberOptions } from 'jssip/lib/UA';
import type { RefObject } from 'react';

import { CallState, Direction } from '@entities/call-item';

export type CallsParkingState = ParkedDialog[];

export interface CallsState {
	list: HistoricalCall[];
	currentId: string | null;
}

export interface ConferenceEndpoint {
	/** Уникальный идентификатор конечного устройства участника */
	entity: string;
	/** Статус подключения endpoint (connected/disconnected/pending) */
	status?: TStatus;
	/** Описание медиапотоков endpoint */
	media: ConferenceMedia[];
}

export interface ConferenceInfo {
	/** URI конференции (focus) */
	entity: string;
	/** Версия conference-info@version */
	version: number;
	/** Полнота snapshot’а (full/partial) */
	state: ConferenceInfoState;
	/** Количество участников (если передано) */
	userCount?: number;
	/** Детальная информация по участникам */
	users: ConferenceUser[];
	/** Сырой XML для отладки */
	rawXml?: string;
}

export type ConferenceInfoState = 'full' | 'partial';

export interface ConferenceMedia {
	/** Идентификатор медиа-блока (id из XML, не обязателен) */
	id?: string;
	/** Тип медиапотока (audio/video/и т.д.) */
	type: string;
	/** Статус или направление медиапотока (sendrecv/recvonly/…) */
	status?: string;
}

export interface ConferenceUser {
	/** Уникальный идентификатор пользователя SIP AoR участника */
	id: string;
	/** Отображаемое имя участника */
	name: string;
	/** Полнота данных по конкретному пользователю (full/partial) */
	state?: ConferenceInfoState;
	/** Статус подключения (connected/disconnected/pending) */
	status: TStatus;
	/** Устройства участника */
	endpoints: ConferenceEndpoint[];
}

export type ContactEvent = (typeof contactEvents)[number];

export const contactEvents = [
	'registered',
	'created',
	'refreshed',
	'expired',
	'unregistered',
	'deactivated',
	'probation',
] as const;

type TIsOnHold = {
	/** Удержание включено у удалённой стороны */
	remote: boolean;
	/** Удержание включено локально */
	local: boolean;
};

export interface DeviceSettingsState {
	selectedAudioInput: string;
	selectedVideoInput: string;
	selectedAudioOutput: string;
	micVolume: number;
	spkVolume: number;
}

export type EventPackageHandler = {
	/** Разрешённые значения заголовка Accept */
	accept: string;
	/** Колбэк обработки NOTIFY */
	onNotify?: (meta: SipEventMeta, payload: NotifyPayload, sub: any) => void;
	/** Колбэк при успешной подписке */
	onAccepted?: (meta: SipEventMeta, sub: any) => void;
	/** Колбэк при переходе подписки в Active */
	onActive?: (meta: SipEventMeta) => void;
	/** Колбэк при ожидании подписки */
	onPending?: (meta: SipEventMeta) => void;
	/** Колбэк при завершении подписки */
	onTerminated?: (meta: SipEventMeta, info: SubscriptionTerminatedPayload, sub: any) => void;
};

export interface HistoricalCall {
	/** Уникальный идентификатор звонка */
	id: string;
	/** SIP AoR абонента (например, sip:alice@example.com) */
	aor: string;
	/** Направление звонка */
	direction: Direction;
	/** Время начала звонка */
	startedAt?: string | null; // timestamp
	/** Время окончания звонка */
	endedAt?: string | null;
	/** display_name удаленной стороны, если пришёл в SIP */
	displayName?: string;
	/** Текущее состояние звонка */
	state: CallState;
	/** Информация о пользователях в конференции */
	conferenceUsers?: ConferenceUser[];
	/** Локальные медиа‑потоки */
	local?: MediaState;
	/** Удалённые медиа‑потоки */
	remote?: MediaState;
	/** Флаг звонка конференции */
	isConference?: boolean;

	// TODO выпилить
	/** Флаг активного шаринга экрана */
	screenShare: boolean;
}

export interface JsSipConfig {
	/** SIP URI пользователя */
	uri: string;
	/** Отображаемое имя SIP пользователя */
	displayName?: string;
	/** Пароль для регистрации */
	password?: string;
	/** Токен для регистрации */
	token?: string;
	/** URL WebSocket для подключения */
	websocketUrl: string;
	/** Использовать Session Timers */
	sessionTimers?: boolean;
	/** Метод refresh для Session Timers (`INVITE` или `UPDATE`) */
	sessionTimersRefreshMethod?: 'INVITE' | 'UPDATE';
	/** Адрес регистратора */
	registrarServer?: string;
	/** Флаг авто‑переподключения */
	autoReconnect?: boolean;
	/** Не создавать автоподписку на `Event: dialog` для парковки вызовов */
	disableCallParkingSubscription?: boolean;
}

export type MediaState = {
	/** Включено ли аудио */
	audio: boolean;
	/** Включено ли видео */
	video: boolean;
};

export type NotifyPayload = {
	/** Финальное ли уведомление */
	isFinal: boolean;
	/** Исходный запрос */
	request: SipRequest;
	/** Тело уведомления */
	body: string | null;
	/** Тип содержимого тела */
	contentType?: string;
};

export interface ParkedDialog {
	/** Идентификатор диалога парковки */
	id: string;
	/** Уникальный Call-ID звонка */
	callId: string;
	/** Локальный SIP-тег */
	localTag: string;
	/** Удалённый SIP-тег */
	remoteTag: string;
	/** Состояние парковки */
	state: ParkingState;
	/** Роль участника (инициатор/получатель) */
	direction: 'initiator' | 'recipient';
	/** Целевой URI инициатора */
	localTarget: string;
	/** Целевой URI получателя */
	remoteTarget: string;
	/** display из remote/identity, если пришёл */
	remoteDisplayName?: string;
	/** Время начала парковки */
	startedWith: string;
	/** Сырой XML с деталями парковки */
	rawXml: string;
}

export interface ParkFailureEvent {
	/** Идентификатор неудачной парковки */
	id: string;
	/** Код ошибки */
	code?: number;
	/** Причина ошибки */
	reason: string;
}

export type ParkingState = 'early' | 'confirmed' | 'terminated';

export interface ParkProgressEvent {
	/** Идентификатор диалога парковки */
	id: string;
	/** Код ответа */
	code?: number;
	/** Текстовое описание стадии */
	reason?: string;
	/** Текущая стадия парковки */
	stage: ParkProgressStage;
}

export type ParkProgressStage = 'trying' | 'progress' | 'accepted' | 'succeeded';

export type RawConferenceInfo = {
	entity?: RawText;
	state?: RawText;
	version?: RawText;
	users?: { user?: RawUser | RawUser[] };
	['conference-state']?: RawConferenceState;
	conferenceState?: RawConferenceState;
};

export type RawConferenceState = {
	['user-count']?: RawText;
};

export type RawEndpoint = {
	entity?: RawText;
	status?: RawText;
	media?: RawMedia | RawMedia[];
};

export type RawMedia = {
	id?: RawText;
	type?: RawText;
	status?: RawText;
	state?: RawText;
};

export type RawText = string | number | { '#text'?: string | number | null } | null | undefined;

export type RawUser = {
	entity?: RawText;
	state?: RawText;
	['display-text']?: RawText;
	displayText?: RawText;
	endpoint?: RawEndpoint | RawEndpoint[];
};

export type ReferHeaderOptions = {
	referToUri?: SipTarget;
	selfUri?: SipTarget;
	contactUri?: string;
	replaces?: string;
	/** Добавить заголовки для слепого REFER без подписки (Refer-Sub: false + Supported: norefersub) */
	disableReferSubscription?: boolean;
	includeSupported?: boolean;
	includeEventRefer?: boolean;
	requireReplaces?: boolean;
	requireTdialog?: boolean;
	targetDialog?: string;
};

export type ReferredByIdentity = {
	aor?: string;
	displayName?: string;
};

export type RegContact = {
	/** Идентификатор контакта */
	id: string;
	/** SIP URI контакта */
	uri: string;
	/** Состояние регистрации */
	state: 'active' | 'terminated';
	/** Событие, которое произошло с контактом */
	event?:
		| 'registered'
		| 'created'
		| 'refreshed'
		| 'deactivated'
		| 'probation'
		| 'unregistered'
		| 'expired';
	/** Время жизни контакта */
	expires?: number;
	/** Длительность текущей регистрации */
	durationRegistered?: number;
};

export interface ReginfoContact {
	/** Идентификатор контакта */
	id: string;
	/** SIP URI контакта */
	uri: string;
	/** Состояние регистрации */
	state: RegistrationState;
	/** Последнее событие регистрации */
	event?: ContactEvent;
	/** Время жизни контакта */
	expires?: number;
	/** Длительность текущей регистрации */
	durationRegistered?: number;
}

export interface ReginfoData {
	/** Версия документа */
	version: number;
	/** Полнота данных */
	rootState: 'full' | 'partial';
	/** Адрес регистрации */
	aor: string;
	/** Состояние регистрации */
	regState: 'active' | 'terminated';
	/** Список контактов */
	contacts: RegContact[];
}

export interface ReginfoDocument {
	/** Пространство имён reginfo */
	xmlns: string;
	/** Версия reginfo */
	version: number;
	/** Полнота данных */
	state: 'full' | 'partial';
	/** Список регистраций */
	registrations: ReginfoRegistration[];
}

export interface ReginfoRegistration {
	/** Адрес регистрации */
	aor: string;
	/** Идентификатор регистрации */
	id: string;
	/** Состояние регистрации */
	state: RegistrationState;
	/** Контакты в рамках регистрации */
	contacts: ReginfoContact[];
}

export type RegistrationState = (typeof registrationStates)[number];

export const registrationStates = ['active', 'terminated'] as const;

export type RegSnapshot = {
	/** SIP адрес регистрации, например sip:alice@example.com */
	aor: string;
	/** Версия reginfo@version */
	version: number;
	/** Только активные контакты регистрации */
	contacts: RegContact[];
	/** Признак, что вся регистрация снята */
	terminated?: boolean;
};

export interface RequestParams {
	/** URI инициатора */
	from_uri: JsSIP.URI;
	/** Отображаемое имя инициатора */
	from_display_name?: string;
	/** SIP-тег инициатора */
	from_tag: string;
	/** URI получателя */
	to_uri: JsSIP.URI;
	/** Отображаемое имя получателя */
	to_display_name?: string;
	/** SIP-тег получателя */
	to_tag?: string;
	/** Call-ID сессии */
	call_id: string;
	/** Порядковый номер CSeq */
	cseq: number;
}

// Session details including media senders/streams
export interface SessionInfo {
	/** Уникальный идентификатор сессии */
	id: string;
	/** Текущая RTCSession */
	session: RTCSession;
	/** Флаги удержания с обеих сторон */
	isOnHold: TIsOnHold;
	/** Направления из последнего pending remote offer (для 491-ретрая) */
	pendingRemoteDir?: Partial<Record<'audio' | 'video', RTCRtpTransceiverDirection>>;
	/** Флаг замещения другой сессии */
	isReplaces?: boolean;
	/** Флаг, что звонок припаркован */
	isParked?: boolean;
	/** Флаг, что удалённая сторона — focus (из Contact:isfocus) */
	isConference?: boolean;
	/** aor пользователя или aor из заголовка Referred-By, если есть */
	aor: string;
	/** display-name пользователя или из заголовка Referred-By, если есть */
	displayName?: string;
	/** Локальный медиапоток */
	localStream: MediaStream;
	/** Удалённый медиапоток */
	remoteStream: MediaStream;
}

export type SessionInternals = {
	id?: RTCSession['id'];
	direction: 'incoming' | 'outgoing';
	_request?: { call_id?: string };
	_dialog?: { id?: { call_id?: string }; _remote_target?: { toString(): string } };
	_from_tag?: string;
	_to_tag?: string;
};

export type SignalQuality = 'bad' | 'normal' | 'good';

export type SipEventMeta = {
	/** Объект подписки, который инициировал событие */
	target: string;
	/** Имя SIP-события */
	eventName: string;
};

export type SipRequest = {
	/** Имя SIP-метода запроса */
	method: string;
	/** Тело запроса */
	body?: string;
	/** Получить значение заголовка */
	getHeader(name: string): string | undefined;
	/** Распарсить заголовок */
	parseHeader(name: string): any;
	/** Проверить наличие заголовка */
	hasHeader(name: string): boolean;
	/** Ответить на запрос */
	reply(code: number, reason?: string | null, extraHeaders?: string[], body?: string): void;
	/** Источник запроса */
	from: { uri: { user?: string } };
	/** Тег получателя */
	to_tag?: string;
};

export type SipTarget = {
	/** SIP AoR */
	aor: string;
	/** Отображаемое имя пользователя */
	displayName?: string;
};

export type SubscriptionTerminatedPayload = {
	/** SIP-код завершения */
	code: number;
	/** Текстовая причина */
	reason?: string;
	/** Через сколько попытаться снова */
	retryAfter?: number;
};

export type TKindTrack = 'audio' | 'video';

export type TStatus = 'pending' | 'connected' | 'disconnected';

export interface UseRemoteSpeakingParams {
	remoteVideoRef: RefObject<HTMLVideoElement | null>;
	isRemoteMute: boolean;
}

export interface VideoBackdropOptions {
	remoteVideoRef: RefObject<HTMLVideoElement | null>;
	isRemoteVideoActive: boolean;
}

export type WatchPreference = {
	/** Целевой ресурс наблюдения */
	target: string;
	/** Разрешённые типы событий */
	accept: string;
	/** Опции подписчика */
	options?: SubscriberOptions;
	/** Автоматический повтор подписки */
	autoRetry: boolean;
	/** Задержка перед повтором */
	retryDelayMs?: number;
};

export type WatchSubscriberOptions = SubscriberOptions & {
	/** Разрешённые типы событий */
	accept?: string;
	/** Автоматический повтор подписки */
	autoRetry?: boolean;
	/** Задержка перед повтором */
	retryDelayMs?: number;
};
